/**
 * ULID 生成 —— 对外暴露的 public_id。
 *
 * 为什么不用自增 ID(docs/13-v1-scope.md §13.4 第 2 条):
 *   - 自增 ID 可枚举,爬虫能遍历所有订单
 *   - 从 ID 能推算出业务量(竞争对手看你的单号就知道日单量)
 *   - 后期想改就是 API 破坏性变更,已发出的追踪链接全部失效
 *
 * 为什么用 ULID 而不是 UUID v4:
 *   ULID 按时间单调递增,作为索引键不会像 v4 那样把 B-tree 打散。
 *   26 个字符,Crockford Base32,不含易混字符(I/L/O/U)。
 *
 * 自己实现而不是引入依赖:算法很短,而 ID 生成是核心路径,
 * 不值得为它承担一个供应链风险。
 */

/** Crockford Base32:去掉了 I L O U,避免手抄和电话报读时混淆 */
const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const ENCODING_LEN = 32;
const TIME_LEN = 10;
const RANDOM_LEN = 16;
/** 48 位时间戳的上限,约公元 10889 年 */
const MAX_TIME = 281474976710655;

function encodeTime(now: number, len: number): string {
  if (!Number.isInteger(now) || now < 0 || now > MAX_TIME) {
    throw new RangeError(`时间戳超出 ULID 范围: ${now}`);
  }
  let str = '';
  for (let i = len - 1; i >= 0; i--) {
    const mod = now % ENCODING_LEN;
    str = ENCODING[mod] + str;
    now = (now - mod) / ENCODING_LEN;
  }
  return str;
}

function randomBytes(n: number): Uint8Array {
  const buf = new Uint8Array(n);
  // Node 18+ 和现代浏览器都有 globalThis.crypto
  globalThis.crypto.getRandomValues(buf);
  return buf;
}

function encodeRandom(len: number): string {
  const bytes = randomBytes(len);
  let str = '';
  for (let i = 0; i < len; i++) {
    // 取字节的低 5 位映射到 Base32。轻微偏置可忽略 —— 80 位随机量远超需求
    str += ENCODING[bytes[i]! % ENCODING_LEN];
  }
  return str;
}

/**
 * 生成 ULID。
 * @param seedTime 仅用于测试注入固定时间,生产不传
 */
export function ulid(seedTime?: number): string {
  const t = seedTime ?? Date.now();
  return encodeTime(t, TIME_LEN) + encodeRandom(RANDOM_LEN);
}

const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;

export function isUlid(s: unknown): s is string {
  return typeof s === 'string' && ULID_RE.test(s);
}

/** 从 ULID 反解生成时间。用于排查问题,不要用于业务逻辑 */
export function ulidTime(id: string): number {
  if (!isUlid(id)) throw new TypeError(`不是合法的 ULID: ${id}`);
  let t = 0;
  for (const ch of id.slice(0, TIME_LEN)) {
    t = t * ENCODING_LEN + ENCODING.indexOf(ch);
  }
  return t;
}

/**
 * 单调 ULID 工厂 —— 同一毫秒内生成多个时保证严格递增。
 *
 * 批量导入 5000 单时,同一毫秒会生成多个 ID。普通 ULID 在同毫秒内
 * 顺序随机,会导致「按 ID 排序」和「按创建时间排序」结果不一致,
 * 游标分页就会漏数据。
 */
export function monotonicFactory(): (seedTime?: number) => string {
  let lastTime = -1;
  let lastRandom = '';

  const incrementBase32 = (str: string): string => {
    const chars = str.split('');
    for (let i = chars.length - 1; i >= 0; i--) {
      const idx = ENCODING.indexOf(chars[i]!);
      if (idx < ENCODING_LEN - 1) {
        chars[i] = ENCODING[idx + 1]!;
        return chars.join('');
      }
      chars[i] = ENCODING[0]!;
    }
    // 16 个字符全部溢出的概率约为 2^-80,实际不会发生
    throw new Error('ULID 随机段溢出');
  };

  return (seedTime?: number): string => {
    const t = seedTime ?? Date.now();
    if (t <= lastTime) {
      lastRandom = incrementBase32(lastRandom);
      return encodeTime(lastTime, TIME_LEN) + lastRandom;
    }
    lastTime = t;
    lastRandom = encodeRandom(RANDOM_LEN);
    return encodeTime(t, TIME_LEN) + lastRandom;
  };
}

/** 进程级单调生成器。业务代码用这个 */
export const nextId = monotonicFactory();

/**
 * 公开追踪码 —— 给收件人的,和 public_id 分开。
 *
 * public_id 虽然不可枚举,但它出现在所有内部接口里;追踪码是唯一
 * 会出现在短信/邮件/包裹面单上的标识,泄露面更大,所以独立生成且更短。
 * 12 位 Base32 ≈ 60 位熵,足够抗猜测。
 */
export function trackingCode(): string {
  return encodeRandom(12);
}

/**
 * 内部单号 —— 可读、可电话报读。
 * 格式:{租户前缀2位}{yyMMdd}{6位序列}
 *
 * 序列来自每租户每日计数器(Redis INCR),所以**能被推算出当日单量**,
 * 只在内部和客户账单用;对收件人一律只给 trackingCode。
 */
export function formatOrderNo(tenantPrefix: string, date: Date, sequence: number): string {
  const prefix = tenantPrefix.toUpperCase().padEnd(2, 'X').slice(0, 2);
  const yy = String(date.getUTCFullYear() % 100).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const seq = String(sequence).padStart(6, '0');
  if (seq.length > 6) throw new RangeError(`单日序列溢出: ${sequence}`);
  return `${prefix}${yy}${mm}${dd}${seq}`;
}
